<script setup lang="ts">
import { __ } from '@@/bits/intl'
import { getAvailableLocales } from '@@/bits/locale'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzScreenSizeAwarePopoverModal from '@@/library/v4/components/OzScreenSizeAwarePopoverModal.vue'
import type { PopoverAnchor } from '@@/types'
import { computed, onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    windowHeight: number
    boxClasses?: string[]
    currentLocale?: string
    anchor?: Partial<PopoverAnchor>
    width?: number
    isPhone?: boolean
    preferredTrayStartHeight?: number
    xBackButton?: boolean
    xCloseButton?: boolean
    xHeader?: boolean
    // Id placed on the dialog so an opener's `aria-controls` can reference it (see LocaleSelector).
    id?: string
  }>(),
  {
    boxClasses: () => [],
    currentLocale: 'en',
    anchor: () => ({}),
    width: 304,
    isPhone: true,
    preferredTrayStartHeight: undefined,
    xBackButton: true,
    xCloseButton: false,
    xHeader: true,
    id: undefined,
  },
)

defineEmits<{
  (name: 'cancel'): void
  (name: 'anchor-update'): void
  (name: 'back'): void
  (name: 'close'): void
  (name: 'change', eventPayload: string): void
}>()

const locales = computed(() => getAvailableLocales())
const currentLocaleItemEl = ref<HTMLElement | null>(null)

function setCurrentLocaleItemRef(el: Element | null, localeHtml: string): void {
  if (el instanceof HTMLElement && localeHtml === props.currentLocale) {
    currentLocaleItemEl.value = el
  }
}

onMounted(() => {
  const el = currentLocaleItemEl.value
  if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'center' })
  }
})
</script>

<template>
  <OzScreenSizeAwarePopoverModal
    :id="id"
    role="dialog"
    :aria-label="__('Language selector')"
    :is-phone="isPhone"
    :is-draggable-drawer="true"
    :x-popover-tip="false"
    :popover-width="width"
    :popover-anchor="anchor"
    :color="OzBoxColors.PrimaryBlur"
    :box-classes="[...boxClasses, 'overflow-hidden']"
    :preferred-tray-start-height="preferredTrayStartHeight"
    :window-height="windowHeight"
    :disable-drag="true"
    @scrim-click="$emit('cancel')"
    @scrim-esc="$emit('cancel')"
    @trigger-anchor-update="$emit('anchor-update')"
  >
    <template v-if="xHeader" #header>
      <div
        :class="[
          'relative',
          // Layout
          'flex items-center justify-center',
          // Spacing
          isPhone ? 'py-3' : 'pt-2 pb-4',
          isPhone ? 'mt-1' : 'mt-2',
        ]"
      >
        <OzIconButton
          v-if="isPhone && xBackButton"
          :color-scheme="OzIconButtonColorScheme.Secondary"
          :size-preset="OzIconButtonSizePreset.H24px"
          :label="__('Go back')"
          icon-name="chevron_left_center"
          class="absolute start-4 px-1"
          @click="$emit('back')"
        />

        <OzIconButton
          v-else-if="xCloseButton"
          :color-scheme="OzIconButtonColorScheme.Secondary"
          :size-preset="OzIconButtonSizePreset.H24px"
          :label="__('Close')"
          icon-name="close"
          class="absolute start-4 px-1"
          @click="$emit('close')"
        />

        <span
          :class="[
            isPhone ? 'text-heading-3' : 'text-heading-4',
            'font-semibold',
            'text-dark-text-100',
            'dark:text-light-text-100',
          ]"
          >{{ __('Choose language') }}</span
        >
      </div>
    </template>
    <template #body>
      <div :class="['overflow-auto scrollbar-thin dark:scrollbar-thin-dark']">
        <!-- Buttons (not links): switching locale PATCHes /session and reloads the page. -->
        <ul :aria-label="__('Language')" :class="['m-0 list-none p-0']">
          <li
            v-for="(locale, index) in locales"
            :key="locale.html"
            :ref="(el) => setCurrentLocaleItemRef(el as Element | null, locale.html)"
          >
            <div class="relative py-1 px-2">
              <OzBaseButton
                :lang="locale.langAttr ?? locale.html"
                :aria-current="currentLocale === locale.html ? 'true' : undefined"
                :data-testid="`${locale.standard}LanguageMenuRow`"
                :class="[
                  // Layout
                  'group',
                  'w-full',
                  'flex',
                  'items-center',
                  'text-start',
                  // Shape & spacing
                  'rounded-xl',
                  'p-2',
                  // Colors
                  'bg-transparent',
                  'text-dark-text-100',
                  'dark:text-light-text-100',
                  // States
                  'transition-colors',
                  'hhover:bg-light-ui-100',
                  'dark:hhover:bg-dark-ui-100',
                  'active:bg-light-ui-100',
                  'dark:active:bg-dark-ui-100',
                  // Focus
                  'focus-visible:outline-hidden',
                  'focus-visible:ring-2',
                  'focus-visible:ring-grape-500',
                  'dark:focus-visible:ring-canary-500',
                  // Sizing
                  isPhone ? 'min-h-[24px]' : 'min-h-[20px]',
                ]"
                @click="$emit('change', locale.html)"
              >
                <span
                  :title="locale.name"
                  :class="[
                    'block',
                    'truncate',
                    'flex-1',
                    'min-w-0',
                    'ps-0.5',
                    isPhone ? 'text-body' : 'text-body-small',
                    'group-active:text-grape-500',
                    'dark:group-active:text-canary-500',
                  ]"
                >
                  <span v-if="locale.standard === 'qs_GB'" class="font-[cursive] italic">{{ locale.name }}</span>
                  <span v-else>{{ locale.name }}</span>
                </span>
                <OzIcon
                  v-if="currentLocale === locale.html"
                  name="checkmark_thicker"
                  :size="isPhone ? 24 : 16"
                  :aria-hidden="true"
                  :class="['ms-2', 'text-grape-500', 'dark:text-canary-500']"
                />
              </OzBaseButton>
            </div>
            <OzDivider v-if="index < locales.length - 1" :color-scheme="OzDividerColorScheme.Solid" class="mx-4" />
          </li>
        </ul>
      </div>
    </template>
  </OzScreenSizeAwarePopoverModal>
</template>
